import { memo } from "react";

import { Heading } from "@components/Heading";
import { Icon } from "@components/Icon";
import { Image } from "@components/Image";
import { Text } from "@react/components/Text";
import { ReviewStars } from "@react/sections/ProductReviews/ReviewStars/ReviewStars.view";
import { type ReviewItemCommonProps } from "@root/tablescape/state";
import { tw } from "classnames";

const styles = {
  base: tw(
    "md:flex",
    "py-6",
    "md:py-10",
    "border-0",
    "border-b",
    "border-solid",
    "border-char-300"
  ),
  metadata: tw(
    "flex-shrink-0",
    "w-44",
    "mr-10",
    "mb-6",
    "md:mb-0",
    "text-primary"
  ),
  authorInfo: tw(
    "mb-1",
    "md:mb-0",
    "flex",
    "items-center",
    "gap-x-2",
    "md:block"
  ),
  reviewStarsMobile: tw("md:hidden", "mb-2", "text-special"),
  reviewStarsDesktop: tw("hidden", "md:block", "mb-2", "text-special"),
  displayName: tw("md:mb-1"),
  verifiedBuyer: tw("inline-flex", "items-center", "md:mb-4"),
  checkmarkCircle: tw(
    "w-3",
    "h-3",
    "mr-1",
    "inline-flex",
    "justify-center",
    "items-center",
    "rounded-full",
    "bg-special"
  ),
  checkmarkIcon: tw("w-2.5", "h-2.5", "text-white"),
  title: tw("mb-2", "text-primary"),
  content: tw("text-primary"),
  images: tw("flex", "flex-wrap", "gap-2.5", "mt-5", "md:mt-6"),
  image: tw("w-17", "h-17", "md:w-22", "md:h-22"),
  reply: tw(
    "mt-6",
    "pl-4",
    "md:pl-6",
    "border-0",
    "border-l-2",
    "border-solid",
    "border-char-500"
  ),
  replyAuthor: tw("mb-2", "text-primary"),
  replyContent: tw("text-primary-muted"),
  reviewedOnProductName: tw("mt-2", "text-primary-muted"),
};

type ReviewItemViewProps = {
  date: string;
  reviewedOnProductName?: string;
} & ReviewItemCommonProps;

const ReviewItemViewBase = ({
  content,
  date,
  displayName,
  images,
  isVerifiedBuyer,
  rating,
  reply,
  reviewedOnProductName,
  title,
}: ReviewItemViewProps) => {
  return (
    <div className={styles.base}>
      <div className={styles.metadata}>
        <div className={styles.reviewStarsMobile}>
          <ReviewStars rating={rating} size="sm" />
        </div>
        <div className={styles.authorInfo}>
          <Text as="body1" className={styles.displayName}>
            {displayName}
          </Text>
          {isVerifiedBuyer && (
            <div className={styles.verifiedBuyer}>
              <span className={styles.checkmarkCircle}>
                <Icon className={styles.checkmarkIcon} name="checkmark" />
              </span>
              <Text>Verified Buyer</Text>
            </div>
          )}
        </div>
        <Text>{date}</Text>
      </div>
      <div>
        <div className={styles.reviewStarsDesktop}>
          <ReviewStars rating={rating} size="sm" />
        </div>
        <Heading as="h5" className={styles.title}>
          {title}
        </Heading>
        <Text as="body1" className={styles.content}>
          {content}
        </Text>
        {reviewedOnProductName && (
          <Text as="body1" className={styles.reviewedOnProductName}>
            Reviewed on: {reviewedOnProductName}
          </Text>
        )}
        {images && (
          <div className={styles.images}>
            {images.map((image) => (
              <Image
                className={styles.image}
                height={90}
                key={image.id}
                src={image.thumb_url}
                width={90}
              />
            ))}
          </div>
        )}
        {reply && (
          <div className={styles.reply}>
            <Text as="body0" className={styles.replyAuthor}>
              Our Place
            </Text>
            <Text className={styles.replyContent}>{reply}</Text>
          </div>
        )}
      </div>
    </div>
  );
};

export const ReviewItemView = memo(ReviewItemViewBase);
